import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { categoryById } from '../data/demo';
import { CategoryTag, VerifiedBadge, StatusBadge } from '../components/Badges';
import { ContactLinks } from '../components/ContactLinks';
import { OnlineDot } from '../components/Presence';
import { ContactActions } from '../components/MemberCard';
import { Star } from 'lucide-react';
import { useI18n } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= Math.round(value) ? 'text-amber-500 fill-amber-500' : 'text-slate-700'} strokeWidth={1.5} />)}
  </span>
);

const BackendReviews = ({ userId }) => {
  const [data, setData] = useState({ reviews: [], rating: null, count: 0 });
  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/reviews/about/${userId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { reviews: [], rating: null, count: 0 }))
      .then(setData).catch(() => {});
  }, [userId]);
  if (!data.reviews?.length) {
    return <div className="sc-card p-6 text-center" data-testid="reviews-empty"><p className="text-sm text-slate-400">Aún no hay valoraciones públicas.</p></div>;
  }
  return (
    <div className="space-y-3" data-testid={`reviews-${userId}`}>
      {data.reviews.map((r) => (
        <div key={r.id} className="sc-card p-5" data-testid={`review-${r.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div className="text-sm font-semibold text-white">{r.author_name} <span className="text-[11px] text-slate-500 font-mono ml-1">· {r.job_title}</span></div>
            <Stars value={r.overall} />
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">{r.comment}</p>
        </div>
      ))}
    </div>
  );
};

const useProfile = (id) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | notfound
  useEffect(() => {
    let active = true;
    setState('loading');
    fetch(`${API}/profile/${id}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (active) { setData(d); setState('ready'); } })
      .catch(() => { if (active) setState('notfound'); });
    return () => { active = false; };
  }, [id]);
  return { data, state };
};

const LoadingShell = () => (
  <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
    <div className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-500 font-mono text-sm" data-testid="detail-loading">Cargando…</div><Footer />
  </div>
);

export const ProfesionalDetailPage = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const { data: pro, state } = useProfile(id);
  if (state === 'loading') return <LoadingShell />;
  if (state === 'notfound' || !pro || pro.account_type === 'company') return <NotFound backTo="/profesionales" label="Volver al directorio" />;
  const cat = categoryById(pro.category);
  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <Link to="/profesionales" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-8">
          <ArrowLeft size={14} /> Directorio de profesionales
        </Link>
        <div className="sc-card p-8 mb-8" data-testid="pro-detail">
          <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
            <div className="w-24 h-24 rounded-full bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 text-3xl font-bold shrink-0 overflow-hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {pro.avatar_url ? <img src={pro.avatar_url} alt={pro.name} className="w-full h-full object-cover" /> : (pro.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-3">
                {cat && <CategoryTag name={cat.name} accent={cat.accent} />}
                {pro.availability && <StatusBadge status={pro.availability} label={pro.availability === 'available' ? t('badge.available') : t('badge.onTour')} />}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{pro.name}<OnlineDot userId={pro.user_id} /></h1>
              {pro.role && <p className="text-slate-400 text-lg mb-3">{pro.role}</p>}
              <div className="flex items-center gap-3 flex-wrap text-sm text-slate-500 font-mono">
                {pro.location && <span><MapPin size={13} className="inline mr-1" />{pro.location}</span>}
                {pro.experience && <span>· {pro.experience}</span>}
                {pro.rating != null && <span className="inline-flex items-center gap-1 text-amber-500"><Star size={13} className="fill-amber-500" /> {pro.rating} · {pro.reviews_count} valoraciones</span>}
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-[#2A2F3D]">
            {pro.bio && (<><h3 className="sc-mono-eyebrow text-amber-500 mb-3">Perfil</h3><p className="text-slate-300 leading-relaxed mb-6">{pro.bio}</p></>)}
            {pro.skills?.length > 0 && (
              <>
                <h3 className="sc-mono-eyebrow text-amber-500 mb-3">{t('label.skills')}</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {pro.skills.map((s) => <span key={s} className="text-sm font-mono px-3 py-1.5 rounded-md border border-[#2A2F3D] text-slate-300">{s}</span>)}
                </div>
              </>
            )}
            {pro.certifications?.length > 0 && (
              <>
                <h3 className="sc-mono-eyebrow text-amber-500 mb-3">{t('label.certifications')}</h3>
                <ul className="space-y-2">
                  {pro.certifications.map((c) => (
                    <li key={c} className="text-sm text-slate-300 flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 mt-2 shrink-0" />{c}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="mb-8"><ContactActions userId={pro.user_id} prefix="pro-contact" /></div>
        {(pro.email || pro.phone) && (
          <div className="sc-card p-6 md:p-8 mb-8" data-testid="pro-contact-block">
            <h3 className="sc-mono-eyebrow text-amber-500 mb-5">Contacto</h3>
            <ContactLinks email={pro.email} phone={pro.phone} testPrefix="pro" />
          </div>
        )}

        <section data-testid="pro-reviews-section">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Valoraciones recientes</h2>
          <BackendReviews userId={pro.user_id} />
        </section>
      </div>
      <Footer />
    </div>
  );
};

export const EmpresaDetailPage = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const { data: c, state } = useProfile(id);
  if (state === 'loading') return <LoadingShell />;
  if (state === 'notfound' || !c || c.account_type !== 'company') return <NotFound backTo="/empresas" label="Volver a empresas" />;
  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <Link to="/empresas" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-8">
          <ArrowLeft size={14} /> Directorio de empresas
        </Link>
        <div className="sc-card p-8 mb-8" data-testid="company-detail">
          <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
            <div className="w-20 h-20 rounded-xl bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 text-2xl font-bold shrink-0 overflow-hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {c.avatar_url ? <img src={c.avatar_url} alt={c.name} className="w-full h-full object-cover" /> : (c.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-3 items-center">
                {c.verified && <VerifiedBadge label={t('badge.verified')} />}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.name}<OnlineDot userId={c.user_id} /></h1>
              <div className="flex items-center gap-3 flex-wrap text-sm text-slate-500 font-mono uppercase tracking-wider mb-4">
                {c.location && <span>{c.location}</span>}
                {c.rating != null && <span className="inline-flex items-center gap-1 text-amber-500 normal-case"><Star size={13} className="fill-amber-500" /> {c.rating} · {c.reviews_count} valoraciones</span>}
              </div>
              {c.specialties?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {c.specialties.map((s) => <span key={s} className="text-xs font-mono px-2 py-1 rounded border border-[#2A2F3D] text-slate-400">{s}</span>)}
                </div>
              )}
            </div>
          </div>
          {c.description && <p className="text-slate-300 leading-relaxed pt-6 border-t border-[#2A2F3D]">{c.description}</p>}
          {!c.verified && (
            <p className="text-[11px] text-slate-500 font-mono mt-4 pt-4 border-t border-[#2A2F3D]">Esta empresa aún no ha completado sus datos de verificación.</p>
          )}
        </div>

        <div className="mb-8"><ContactActions userId={c.user_id} prefix="company-contact" /></div>
        {(c.contactEmail || c.contactPhone || c.website || c.address) && (
          <div className="sc-card p-6 md:p-8 mb-8" data-testid="company-contact-block">
            <h3 className="sc-mono-eyebrow text-amber-500 mb-5">Contacto</h3>
            <ContactLinks email={c.contactEmail} phone={c.contactPhone} website={c.website} address={c.address} testPrefix="company" />
          </div>
        )}

        <section data-testid="company-reviews-section">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Valoraciones recientes</h2>
          <BackendReviews userId={c.user_id} />
        </section>
      </div>
      <Footer />
    </div>
  );
};

const NotFound = ({ backTo, label }) => (
  <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
    <div className="max-w-4xl mx-auto px-4 py-24 text-center">
      <h1 className="text-3xl font-bold text-white mb-4">No encontrado</h1>
      <Link to={backTo} className="text-amber-500 font-mono uppercase tracking-wider text-sm">← {label}</Link>
    </div><Footer /></div>
);
